import React from "react";
import { FaArrowRight, FaShieldHalved, FaBolt, FaUserTie } from "react-icons/fa6";
import { BTN_SIZE, Container, H1_SIZE } from "../home/ui";

// Hero artwork (laptop, phone and tech tiles on a white background), shown as an image in the right column on
// desktop (xl+) and below the text on mobile/tablet. Its edges are faded so the white never shows as a box.
// File: public/img/hero-bg2.jpg.
// Text, buttons and badges use vw sizes on xl+ (baseline: the design at a ~1707px wide window, i.e. 90% zoom on a
// 1536px laptop), so the hero keeps the same proportions at any zoom level / screen width.
const heroImg = "/img/hero-bg3.jpg";

const fadeX = "linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)";
const fadeY = "linear-gradient(to bottom, transparent 0%, #000 8%, #000 92%, transparent 100%)";
const edgeFade = {
  WebkitMaskImage: `${fadeX}, ${fadeY}`,
  WebkitMaskComposite: "source-in",
  maskImage: `${fadeX}, ${fadeY}`,
  maskComposite: "intersect",
};

const badges = [
  { icon: FaShieldHalved, title: "Secure", text: "Threat protection", tone: "bg-[#DFF7EC] text-[#10B981]" },
  { icon: FaBolt, title: "Reliable", text: "99.9% uptime", tone: "bg-[#FFEDD5] text-[#F97316]" },
  { icon: FaUserTie, title: "Expert Support", text: "Always here for you", tone: "bg-[#E3EDFF] text-[#2563EB]" },
];

const MaintHero = () => (
  <section className="relative overflow-hidden bg-gradient-to-r from-[#FBFCFE] via-[#F6F9FF] to-[#EEF3FE] pb-[48px] pt-[110px] xl:flex xl:min-h-[min(38vw,760px)] xl:items-center xl:pb-[40px] xl:pt-[calc(clamp(76px,5.83vw,112px)+24px)]">
    <Container className="relative grid w-full max-w-[1900px] items-center gap-[32px] xl:grid-cols-[minmax(0,44%)_1fr] xl:gap-[clamp(24px,2.5vw,56px)] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
      <div className="m-hero">
        <span className="inline-flex items-center rounded-full bg-[#E3EDFF] px-[16px] py-[6px] text-[13px] font-semibold uppercase tracking-[0.04em] text-[#1E4DFF] xl:px-[clamp(12px,0.94vw,20px)] xl:py-[clamp(4px,0.35vw,8px)] xl:text-[clamp(11px,0.76vw,16px)]">
          Website Maintenance &amp; Support
        </span>

        <h1 className={`mt-[18px] xl:mt-[1.05vw] font-extrabold tracking-tight text-[#0B1A4A] ${H1_SIZE}`}>
          Keep Your Website
          {" "}<br />
          Always <span className="text-[#1E6BFF]">Running</span>
          {" "}<br />
          <span className="text-[#1E6BFF]">Smoothly</span>
        </h1>

        <p className="mt-[18px] max-w-[540px] text-[16px] leading-[1.6] text-slate-600 sm:text-[17px] xl:mt-[1.05vw] xl:max-w-[31.6vw] xl:text-[clamp(13px,1.05vw,21px)]">
          We provide reliable website maintenance and support services to ensure your website stays secure, up to date, fast
          and always accessible.
        </p>

        <div className="m-btns mt-[28px] flex flex-wrap items-center gap-[16px] xl:mt-[1.64vw] xl:gap-[0.94vw]">
          <a
            href="/contact-us"
            className={`group inline-flex items-center rounded-full bg-[#1E5BFF] font-medium text-white shadow-[0_14px_30px_-14px_rgba(30,91,255,0.8)] transition hover:brightness-110 ${BTN_SIZE}`}
          >
            Get Started
            <FaArrowRight className="text-[13px] xl:text-[clamp(11px,0.76vw,15px)] transition group-hover:translate-x-1" />
          </a>
          <a
            href="/contact-us"
            className={`inline-flex items-center rounded-full border-[1.5px] border-solid border-[#7FA3FF] bg-white/70 font-medium text-[#1E4DFF] transition hover:bg-[#EEF3FF] ${BTN_SIZE}`}
          >
            Talk to Expert
          </a>
        </div>

        <div className="mt-[36px] flex flex-wrap gap-x-[32px] gap-y-[18px] xl:mt-[2.11vw] xl:flex-nowrap xl:gap-x-[1.87vw]">
          {badges.map((b) => (
            <div key={b.title} className="flex items-center gap-[12px] xl:gap-[0.7vw]">
              <span className={`inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[12px] text-[18px] xl:h-[clamp(34px,2.46vw,50px)] xl:w-[clamp(34px,2.46vw,50px)] xl:rounded-[0.7vw] xl:text-[clamp(14px,1.05vw,21px)] ${b.tone}`}>
                <b.icon />
              </span>
              <div>
                <p className="whitespace-nowrap text-[14.5px] font-semibold text-[#0B1A4A] xl:text-[clamp(12px,0.85vw,17px)]">{b.title}</p>
                <p className="mt-[2px] whitespace-nowrap text-[12.5px] text-slate-500 xl:text-[clamp(10.5px,0.73vw,15px)]">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center xl:justify-end">
        <img
          src={heroImg}
          alt="Laptop and phone with code, surrounded by technology icons"
          className="h-auto w-full max-w-[640px] select-none object-contain xl:max-w-none"
          style={edgeFade}
        />
      </div>
    </Container>
  </section>
);

export default MaintHero;
